import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';

// ─── HuPath v2 Design Tokens ──────────────────────
const HT = {
  yellow: '#FFD166', yellowSoft: '#FFE9A8', yellowBg: '#FFF8E7', yellowShadow: '#E0B554',
  green: '#88C057', greenDark: '#5E9E3E', greenSoft: '#D4E8C0', greenBg: '#E8F3D9', greenShield: '#C9E4A5',
  cream: '#FFF8E7',
  coral: '#FF8C42', coralDark: '#E76F51', coralPress: '#B8431E',
  pinkBg: '#FCE4E8', pink: '#F4A6B8',
  brown: '#8B6F47', brownDark: '#6B4F2B',
  text: '#2D2D2D', textSoft: '#6B6B6B', textMuted: '#9A9A9A',
  border: '#E8E0CC', white: '#FFFFFF', teal: '#5AC0B5',
  plum: '#5B2E5E', aqua: '#3DC9B5', accent: '#FFD23F',
};
const FONT_DISPLAY = '"Fredoka", "Baloo 2", "Nunito", sans-serif';
const FONT_BODY = '"Nunito", "Baloo 2", sans-serif';
const SHADOW_SM = '0 2px 6px rgba(139,111,71,0.08)';
const SHADOW_MD = '0 4px 16px rgba(139,111,71,0.10)';

const tierFor = (percent) => {
  if (percent >= 80) return { color: HT.aqua, bg: HT.greenBg, label: '🎉' };
  if (percent >= 60) return { color: HT.yellow, bg: HT.yellowBg, label: '💪' };
  return { color: HT.coral, bg: HT.pinkBg, label: '📚' };
};

const FullTestResultSummary = ({ result, attemptId }) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (!result) return null;

  const {
    percentage_score,
    total_score,
    max_score,
    correct_answers, // eslint-disable-line no-unused-vars
    incorrect_answers, // eslint-disable-line no-unused-vars
    skipped_answers,
    total_questions,
    section_scores,
  } = result;

  const overallTier = tierFor(percentage_score);

  // Detect any pending Part 7 grading across all sections
  const hasPendingPart7 = (section_scores || []).some(section =>
    (section.questions || []).some(q => (q?.grading_status === 'pending') || (q?.user_answer?.grading_status === 'pending'))
  );

  return (
    <div style={{
      minHeight: '100vh', background: HT.cream, color: HT.text, fontFamily: FONT_BODY,
      padding: '24px 16px', position: 'relative',
    }}>
      <div style={{ maxWidth: 960, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 22 }}>

        {/* Header badge */}
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, marginTop: 18 }}>
          <div style={{
            width: 88, height: 88, borderRadius: 24,
            background: HT.yellow,
            border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transform: 'rotate(-3deg)',
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 48, color: HT.text, fontVariationSettings: "'FILL' 1" }}>
              workspace_premium
            </span>
          </div>
          <h1 style={{ fontSize: 42, fontWeight: 800, fontFamily: FONT_DISPLAY, margin: 0, letterSpacing: -1.2, lineHeight: 1.1, color: HT.text }}>
            {t('testCompleted')}
          </h1>
          <p style={{ fontSize: 16, fontWeight: 600, color: HT.plum, margin: 0 }}>
            {t('excellentResult')}
          </p>
        </div>

        {/* Overall Score Card */}
        <div style={{
          background: overallTier.bg,
          border: `1px solid ${HT.border}`, boxShadow: SHADOW_MD,
          borderRadius: 22, padding: '22px 24px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 24,
          flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 220px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: HT.plum, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 6 }}>
              {t('overallScore')}
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontSize: 64, fontWeight: 800, fontFamily: FONT_DISPLAY, color: HT.text, letterSpacing: -2, lineHeight: 1 }}>
                {total_score}
              </span>
              <span style={{ fontSize: 22, fontWeight: 800, color: HT.textMuted }}>
                / {max_score}
              </span>
              <span style={{ marginLeft: 8, fontSize: 34 }}>{overallTier.label}</span>
            </div>
          </div>

          <div style={{
            background: HT.white, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
            borderRadius: 16, padding: '14px 18px',
            display: 'flex', alignItems: 'center', gap: 18,
          }}>
            <Stat value={`${percentage_score.toFixed(0)}%`} label={t('accuracy')} color={overallTier.color} />
            <div style={{ width: 1, height: 40, background: HT.border }} />
            <Stat value={result.attempted_questions ?? (total_questions - skipped_answers)} label={t('attempted')} color={HT.text} />
            <div style={{ width: 1, height: 40, background: HT.border }} />
            <Stat value={skipped_answers} label={t('skipped')} color={HT.plum} />
          </div>
        </div>

        {/* Pending Part 7 banner */}
        {hasPendingPart7 && (
          <div style={{
            background: HT.yellowBg, border: `1px solid ${HT.yellowShadow}`,
            borderRadius: 12, padding: '10px 16px',
            display: 'flex', alignItems: 'center', gap: 10,
            fontSize: 13, fontWeight: 700, color: HT.brown,
            fontFamily: FONT_BODY,
          }}>
            <span style={{ fontSize: 18 }}>✍️</span>
            Điểm tạm tính — Part 7 đang chờ chấm.
          </div>
        )}

        {/* Section breakdown */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <h3 style={{
            fontSize: 22, fontWeight: 800, fontFamily: FONT_DISPLAY, margin: 0,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <span style={{
              width: 40, height: 40, borderRadius: 11,
              background: HT.plum, color: HT.white,
              border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <span className="material-symbols-outlined" style={{ fontSize: 22, fontVariationSettings: "'FILL' 1" }}>analytics</span>
            </span>
            {t('sectionDetails')}
          </h3>

          <div style={{
            display: 'grid', gap: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          }}>
            {section_scores.map((section, idx) => {
              const pct = (section.score / section.max_score) * 100;
              const tier = tierFor(pct);
              const isPerfect = section.number_shields === 5;

              // Locate the Part 7 question (story writing) if present in this section
              const part7Q = (section.questions || []).find(
                q => q?.exercise_type === 'reading_writing_flyer_part_7'
              );
              const part7Answer = part7Q?.user_answer;
              const part7Pending = part7Answer?.grading_status === 'pending' || part7Q?.grading_status === 'pending';

              return (
                <div
                  key={`${section.section_id}-${idx}`}
                  style={{
                    background: HT.white, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                    borderRadius: 18, padding: '16px 18px',
                    display: 'flex', flexDirection: 'column', gap: 12,
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                    <div>
                      <h4 style={{ fontSize: 16, fontWeight: 800, margin: 0, color: HT.text, lineHeight: 1.2 }}>
                        {section.section_title}
                      </h4>
                      <div style={{ marginTop: 4, fontSize: 12, fontWeight: 700, color: HT.textSoft }}>
                        {t('scoreLabel')}:{' '}
                        <span style={{ color: HT.text, fontWeight: 800 }}>{section.score}</span>{' '}/ {section.max_score}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 3 }}>
                      {[1, 2, 3, 4, 5].map((s) => (
                        <span
                          key={s}
                          className="material-symbols-outlined"
                          style={{
                            fontSize: 20,
                            color: s <= section.number_shields ? HT.yellow : '#E5E0D5',
                            fontVariationSettings: "'FILL' 1",
                            textShadow: s <= section.number_shields ? `0 1px 2px rgba(139,111,71,0.15)` : 'none',
                          }}
                        >shield</span>
                      ))}
                    </div>
                  </div>

                  {/* Progress bar */}
                  <div style={{
                    height: 12, background: HT.cream,
                    border: `1px solid ${HT.border}`, borderRadius: 99,
                    padding: 1, overflow: 'hidden', position: 'relative',
                  }}>
                    <div style={{
                      width: `${pct}%`, height: '100%', background: tier.color,
                      borderRadius: 99, transition: 'width 0.7s ease',
                    }} />
                  </div>

                  {isPerfect && (
                    <div style={{
                      background: HT.yellowSoft, border: `1px solid ${HT.border}`, boxShadow: SHADOW_SM,
                      borderRadius: 10, padding: '6px 12px',
                      fontSize: 11, fontWeight: 800, color: HT.text,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                      letterSpacing: 0.5,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 16, fontVariationSettings: "'FILL' 1" }}>celebration</span>
                      {t('perfectSection')}
                    </div>
                  )}

                  {/* Part 7 story writing card */}
                  {part7Q && (
                    <div style={{
                      background: HT.greenBg, border: `1px solid ${HT.greenSoft}`, borderRadius: 12,
                      padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10,
                      fontFamily: FONT_BODY,
                    }}>
                      {/* Header row */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 18 }}>✍️</span>
                        <span style={{ fontSize: 13, fontWeight: 800, color: HT.greenDark, letterSpacing: 0.3 }}>
                          Part 7 — Viết chuyện
                        </span>
                        {part7Pending ? (
                          <span style={{
                            background: HT.yellowSoft, border: `1px solid ${HT.yellowShadow}`,
                            borderRadius: 99, padding: '2px 10px',
                            fontSize: 11, fontWeight: 800, color: HT.brown,
                          }}>
                            Đang chờ AI chấm
                          </span>
                        ) : (
                          <span style={{
                            background: HT.teal, borderRadius: 99, padding: '2px 10px',
                            fontSize: 13, fontWeight: 800, color: HT.white,
                          }}>
                            {part7Q.score_earned ?? 0} / {part7Q.max_points ?? 5} điểm
                          </span>
                        )}
                      </div>

                      {/* Student's story */}
                      {part7Answer?.answer && (
                        <div style={{
                          background: HT.white, border: `1px solid ${HT.border}`, borderRadius: 10,
                          padding: '10px 14px', fontSize: 13, color: HT.text, lineHeight: 1.6,
                          fontStyle: 'italic',
                        }}>
                          {part7Answer.answer}
                        </div>
                      )}

                      {part7Pending ? (
                        <div style={{ fontSize: 12, color: HT.brown, fontWeight: 600 }}>
                          Điểm tổng tạm tính — Part 7 đang chờ chấm.
                        </div>
                      ) : (
                        <>
                          {/* Bilingual feedback */}
                          {part7Answer?.feedback_vi && (
                            <div style={{ fontSize: 13, color: HT.text, fontWeight: 600, lineHeight: 1.6 }}>
                              {part7Answer.feedback_vi}
                            </div>
                          )}
                          {part7Answer?.feedback_en && (
                            <div style={{ fontSize: 12, color: HT.textSoft, fontStyle: 'italic', lineHeight: 1.5 }}>
                              {part7Answer.feedback_en}
                            </div>
                          )}
                          {/* Dimension labels */}
                          {(part7Answer?.pictures_addressed != null || part7Answer?.event_progression != null || part7Answer?.reader_effort != null) && (
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
                              {part7Answer?.pictures_addressed != null && (
                                <span style={{
                                  background: HT.greenSoft, borderRadius: 99, padding: '2px 10px',
                                  fontSize: 11, fontWeight: 700, color: HT.greenDark,
                                }}>
                                  Minh họa: {part7Answer.pictures_addressed}/5
                                </span>
                              )}
                              {part7Answer?.event_progression != null && (
                                <span style={{
                                  background: HT.greenSoft, borderRadius: 99, padding: '2px 10px',
                                  fontSize: 11, fontWeight: 700, color: HT.greenDark,
                                }}>
                                  Mạch truyện: {part7Answer.event_progression}/5
                                </span>
                              )}
                              {part7Answer?.reader_effort != null && (
                                <span style={{
                                  background: HT.greenSoft, borderRadius: 99, padding: '2px 10px',
                                  fontSize: 11, fontWeight: 700, color: HT.greenDark,
                                }}>
                                  Nỗ lực: {part7Answer.reader_effort}/5
                                </span>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex', gap: 12, paddingTop: 14,
          borderTop: `1px dashed ${HT.border}`, marginTop: 6,
          flexWrap: 'wrap',
        }}>
          <button
            onClick={() => navigate(`/full-mock-test/${attemptId}/review`)}
            style={{
              flex: '1 1 220px',
              padding: '14px 22px', borderRadius: 14,
              border: `1px solid ${HT.coralDark}`,
              background: HT.coral, color: HT.white,
              boxShadow: SHADOW_SM,
              fontFamily: FONT_BODY, fontSize: 15, fontWeight: 800,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              letterSpacing: 0.3,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>rate_review</span>
            {t('reviewAttempt')}
          </button>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              flex: '1 1 220px',
              padding: '14px 22px', borderRadius: 14,
              border: `1px solid ${HT.border}`,
              background: HT.white, color: HT.text,
              boxShadow: SHADOW_SM,
              fontFamily: FONT_BODY, fontSize: 15, fontWeight: 800,
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              letterSpacing: 0.3,
            }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 20 }}>home</span>
            {t('backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ value, label, color }) => (
  <div style={{ textAlign: 'center', minWidth: 72 }}>
    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    <div style={{ marginTop: 4, fontSize: 9, fontWeight: 800, color: HT.textMuted, letterSpacing: 1.2, textTransform: 'uppercase' }}>
      {label}
    </div>
  </div>
);

export default FullTestResultSummary;
